import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Preferences } from '@capacitor/preferences';
import PlanSelection, { FormInput } from "./PlanSelection";
import { useDispatch } from "react-redux";
import { ThunkDispatchType, actions } from "../../../store";
import { 
  BILLING_INTERVAL, PAGE_URL, TIER, NEW_TIER, PRICING, EVENT_TYPE, NEW_TIER_DETAILS 
} from "../../../constants";
import { getCheckoutSessionId, redirectToCheckout, trackPurchase } from "../../../utils/paymentUtils";
import { useCabinetText } from "../../../CabinetContext";
import { trackEventWithExtra } from "../../../utils/appAnalyticsUtils";
import { useNavigate } from "react-router";


const handleTrackPurchase = (
  value: number, currency: string, tier: string, quantity: number, confirmation: string
) => {
  const purchaseData = {
    transaction_id: confirmation,
    value: value * quantity,
    currency,
    items: [{
      id: 0,
      name: tier,
      brand: "Cabinet",
      category: "Premium Subscription",
      quantity,
      price: value,
    }],
  };
  trackPurchase(purchaseData);
  trackEventWithExtra({ eventName: EVENT_TYPE.PURCHASE, extra: purchaseData });
};


export interface PlanSelectionContainerProps {
  onFinish: () => void;
  options?: { confirmation?: string; qty?: number; sessionId?: string; val?: number; cur?: string; tier?: string };
}

const PlanSelectionContainer = ({ onFinish, options }: PlanSelectionContainerProps): ReactElement => {
  const dispatch = useDispatch<ThunkDispatchType>();
  const navigate = useNavigate();
  const [dialogError, setDialogError] = useState<string | undefined>();
  const [defaultSelection, setDefaultSelection] = useState<{ planTier?: TIER, billedAnnually?: boolean } | undefined>();
  const logout = useCallback(async () => {
    await dispatch(actions.auth.logout());
    navigate(PAGE_URL.LOGIN);
  }, [dispatch, navigate]);

  const [coreDetails, strategicDetails] = useCabinetText([
    'onboard-core-features',
    'onboard-strategic-features',
  ]);


  const planOptions = useMemo(() => {
    return [
      {
        id: NEW_TIER.CORE,
        name: 'Core',
        costMonthly: NEW_TIER_DETAILS[NEW_TIER.CORE].monthlyPrice,
        // costAnnually is per month when billed annually
        costAnnually: NEW_TIER_DETAILS[NEW_TIER.CORE].annualPrice / 12,
        caption: '',
        details: coreDetails,
        buttonText: 'Choose this plan',
        planDescription: 'For EAs or admins supporting a single leader who need dependable scheduling tools',
      },
      {
        id: NEW_TIER.STRATEGIC,
        name: 'Strategic',
        costMonthly: NEW_TIER_DETAILS[NEW_TIER.STRATEGIC].monthlyPrice,
        // costAnnually is per month when billed annually
        costAnnually: NEW_TIER_DETAILS[NEW_TIER.STRATEGIC].annualPrice / 12,
        caption: '',
        details: strategicDetails,
        buttonText: 'Choose this plan',
        planDescription:
          'For power users or EA teams supporting multiple leaders with strategic operations'
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreDetails.join(','), strategicDetails.join(',')]);

  const handlePaymentStartFailed = () => {
    setDialogError(`Uh oh - Something went wrong! Try again \
                    or send an SOS to our team at help@joincabinet.com.`);
  };

  const checkDefaultSelection = useCallback(async () => {
    const { value: planTier } = await Preferences.get({ key: 'planTier' });
    const { value: planInterval } = await Preferences.get({ key: 'planInterval' });
    const defaults: { planTier?: TIER, billedAnnually?: boolean } = {};
    if (planTier) {
      defaults.planTier = planTier as TIER;
    }
    if (planInterval) {
      defaults.billedAnnually = planInterval === BILLING_INTERVAL.YEAR;
    }
    setDefaultSelection(defaults);
  }, []);

  useEffect(() => {
    checkDefaultSelection();
  }, [checkDefaultSelection]);

  // finish this step if checkout was successful
  useEffect(() => {
    if (options) {
      // TODO: verify this is working
      const { confirmation, qty, sessionId, val, cur, tier } = options;
      if (confirmation && qty && sessionId && val != null && cur && tier) {
        onFinish();
        handleTrackPurchase(val, cur, tier, qty, confirmation);
      }
    }
  }, [options, onFinish]);

  const handleFinish = async (values: FormInput) => {
    const { billedAnnually, planTier } = values;
    const billingInterval = billedAnnually ? BILLING_INTERVAL.YEAR : BILLING_INTERVAL.MONTH;

    trackEventWithExtra({ 
      eventName: EVENT_TYPE.ONBOARD_CONTINUE_WITH_PLAN, 
      extra: {tier: planTier} 
    });

    if (planTier === NEW_TIER.BASIC) {
      onFinish();
    } else if ([NEW_TIER.CORE, NEW_TIER.STRATEGIC].includes(planTier)) {
      // Coupon codes are available in the Stripe Checkout UI for first-time purchases so we don't handle it here
      // Right now we don't handle more than 1 seat purchases during onboarding
      const quantity = 1;
      const cancelUrl = `${window.location.origin}${PAGE_URL.SIGNUP}?stripeCancel=true`;
      try {
        const session = await getCheckoutSessionId(
          '', planTier, billingInterval, quantity, window.location.href, PAGE_URL.SIGNUP, cancelUrl
        );
        if (session?.status === 200) {
          if (!session.data.active_sub_id && session.data.session_id) {
            redirectToCheckout(session.data.session_id);
          } else if (session.data.active_sub_id) {
            await dispatch(actions.organization.changeSubscription(planTier, billingInterval, quantity, null));
            onFinish();
          } else {
            handlePaymentStartFailed();
          }
        }
        
      } catch (error) {
        handlePaymentStartFailed();
      }
    } else {
      window.open(PRICING, '_blank');
    }
  };

  return (
    <PlanSelection
      defaultSelection={defaultSelection}
      planOptions={planOptions}
      dialogError={dialogError}
      onDismissError={() => setDialogError(undefined)}
      onFinish={handleFinish}
      onLogout={logout}
    />
  );
};

export default PlanSelectionContainer;
